import React, { useState, useRef, useEffect } from "react";
import { 
  Layout, Typography, Card, Space, Button, 
  Row, Col, Divider, Upload, message, Spin
} from "antd";
import { 
  PlayCircleOutlined, UploadOutlined, 
  CameraOutlined, DownloadOutlined, 
  GithubOutlined, InfoCircleOutlined 
} from "@ant-design/icons";

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

export default function HuskyTracking() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null); // 存储真实的File对象
  const [detectionResults, setDetectionResults] = useState([]);
  const [isCameraActive, setIsCameraActive] = useState(false); // 摄像头是否激活
  const [cameraStream, setCameraStream] = useState(null); // 存储摄像头流
  
  // Canvas和Video引用
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const cameraCanvasRef = useRef(null);

  

  // 处理图像上传
  const handleImageUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('请上传有效的图像文件（支持JPG、PNG等格式）');
      return false;
    }
    
    // 关闭摄像头（如果正在使用）
    stopCamera();
    
    // 清空上一次的检测结果和画布
    setDetectionResults([]);
    clearCanvas();
    
    // 读取并显示图像
    const reader = new FileReader();
    reader.onload = (e) => {
      setCurrentImage(e.target.result);
      setUploadedFile(file); // 保存真实的File对象
    };
    reader.readAsDataURL(file);
    
    return false; // 阻止自动上传
  };


  // 后端API地址
  const API_BASE_URL = 'https://api.xizaijianqishu.top';

  // 直接在组件内实现API请求
  const handleDetection = async () => {
    if (!uploadedFile) {
      message.warning('请先上传图像');
      return;
    }

    setIsProcessing(true);
    
    try {
      // 创建FormData对象
      const formData = new FormData();
      formData.append('image', uploadedFile);

      // 创建一个AbortController用于中断请求
      const controller = new AbortController();
      const signal = controller.signal;

      // 创建超时Promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          controller.abort(); // 中断请求
          reject(new Error('请求超时：结果未找到'));
        }, 10000); // 10秒超时
      });

      // 创建API请求Promise
      const apiRequestPromise = fetch(`${API_BASE_URL}/api/detect-husky`, {
        method: 'POST',
        body: formData,
        signal: signal // 传递信号用于中断
      });

      // 使用Promise.race让请求和超时竞争
      const response = await Promise.race([apiRequestPromise, timeoutPromise]);

      // 检查响应状态
      if (!response.ok) {
        throw new Error(`HTTP错误：${response.status}`);
      }

      // 解析JSON响应
      const result = await response.json();
      
      if (result.success) {
        // 存储检测结果
        setDetectionResults(result.data.detectionResults);
        message.success(result.message || '检测完成！');
      } else {
        message.error(result.message || '检测失败');
      }
    } catch (error) {
      // 处理请求中断错误
      if (error.name === 'AbortError') {
        message.error('请求超时：结果未找到');
      } else {
        message.error('检测失败：' + error.message);
      }
      console.error('API调用错误:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 实现前端下载功能 - 直接从Canvas生成图像
  const handleDownload = () => {
    if (!currentImage || !detectionResults.length || !canvasRef.current || !imgRef.current) {
      message.warning('没有可下载的检测结果');
      return;
    }
    
    try {
      setIsProcessing(true);
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const imgElement = imgRef.current;
      
      // 创建一个新的Canvas用于下载
      const downloadCanvas = document.createElement('canvas');
      const downloadCtx = downloadCanvas.getContext('2d');
      
      // 设置Canvas尺寸为原始图像尺寸
      downloadCanvas.width = imgElement.naturalWidth;
      downloadCanvas.height = imgElement.naturalHeight;
      
      // 绘制原始图像
      downloadCtx.drawImage(imgElement, 0, 0);
      
      // 绘制检测框（使用原始图像尺寸，无需缩放）
      detectionResults.forEach((result) => {
        const { bbox, className, confidence } = result;
        
        if (!bbox || bbox.length !== 4) {
          return;
        }
        
        const x = Math.round(bbox[0]);
        const y = Math.round(bbox[1]);
        const width = Math.round(bbox[2]);
        const height = Math.round(bbox[3]);
        
        // 绘制边框
        downloadCtx.strokeStyle = '#FF0000';
        downloadCtx.lineWidth = 3;
        downloadCtx.strokeRect(x, y, width, height);
        
        // 绘制标签背景
        downloadCtx.fillStyle = 'rgba(255, 0, 0, 0.8)';
        const text = `${className} ${confidence.toFixed(2)}`;
        const textWidth = downloadCtx.measureText(text).width + 10;
        downloadCtx.fillRect(x, y - 25, textWidth, 25);
        
        // 绘制标签文字
        downloadCtx.fillStyle = '#FFFFFF';
        downloadCtx.font = '14px Arial';
        downloadCtx.fillText(text, x + 5, y - 8);
      });
      
      // 将Canvas转换为图像URL
      downloadCanvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas转换失败');
        }
        
        // 创建下载链接
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `husky-detection-${Date.now()}.jpg`;
        
        // 触发下载
        document.body.appendChild(link);
        link.click();
        
        // 清理
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        message.success('下载成功！');
      }, 'image/jpeg', 0.9); // 使用JPEG格式，质量0.9
    } catch (error) {
      message.error('下载失败：' + error.message);
      console.error('下载错误:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 添加ref引用图像元素
  const imgRef = useRef(null);

  // 在Canvas上绘制检测结果
  const drawDetectionResults = () => {
    if (!currentImage || !detectionResults.length || !canvasRef.current || !imgRef.current) {
      console.log('绘制条件不满足:', { currentImage, detectionResultsLength: detectionResults.length, canvas: !!canvasRef.current, imgRef: !!imgRef.current });
      return;
    }

    console.log('开始绘制检测框，结果数量:', detectionResults.length);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imgElement = imgRef.current;
    
    // 直接设置Canvas尺寸为图像显示尺寸
    const imgDisplayWidth = imgElement.offsetWidth;
    const imgDisplayHeight = imgElement.offsetHeight;
    
    // 清除Canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // 重新设置Canvas尺寸
    canvas.width = imgDisplayWidth;
    canvas.height = imgDisplayHeight;
    
    // 计算缩放因子
    const scaleX = imgDisplayWidth / imgElement.naturalWidth;
    const scaleY = imgDisplayHeight / imgElement.naturalHeight;
    
    console.log('图像原始尺寸:', imgElement.naturalWidth, imgElement.naturalHeight);
    console.log('图像显示尺寸:', imgDisplayWidth, imgDisplayHeight);
    console.log('缩放因子:', scaleX, scaleY);
    
    // 绘制检测框（使用缩放后的坐标）
    detectionResults.forEach((result, index) => {
      const { bbox, className, confidence } = result;
      console.log('检测结果', index, ':', { bbox, className, confidence });
      
      // 应用缩放因子到边界框坐标
      const x = Math.round(bbox[0] * scaleX);
      const y = Math.round(bbox[1] * scaleY);
      const width = Math.round(bbox[2] * scaleX);
      const height = Math.round(bbox[3] * scaleY);
      
      console.log('绘制框坐标:', { x, y, width, height });
      
      // 绘制边框
      ctx.strokeStyle = '#FF0000';
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, width, height);
      
      // 绘制标签背景
      ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
      const text = `${className} ${confidence.toFixed(2)}`;
      const textWidth = ctx.measureText(text).width + 10;
      ctx.fillRect(x, y - 25, textWidth, 25);
      
      // 绘制标签文字
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '14px Arial';
      ctx.fillText(text, x + 5, y - 8);
    });
  };

  // 添加useEffect监听检测结果变化，自动绘制检测框
  useEffect(() => {
    if (detectionResults.length > 0) {
      console.log('检测结果已更新，准备绘制');
      // 使用requestAnimationFrame确保在DOM更新后绘制
      requestAnimationFrame(() => {
        drawDetectionResults();
      });
    }
  }, [detectionResults, currentImage]);

  // 清空画布
  const clearCanvas = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    if (cameraCanvasRef.current) {
      const canvas = cameraCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setDetectionResults([]);
  };
  
  // 启动摄像头
  const startCamera = async () => {
    try {
      // 请求摄像头权限
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      });
      
      // 设置视频流
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      setCameraStream(stream);
      setIsCameraActive(true);
      setCurrentImage(null);
      setUploadedFile(null);
      clearCanvas();
      message.success('摄像头已启动');
    } catch (error) {
      message.error('无法访问摄像头：' + error.message);
      console.error('摄像头访问错误:', error);
    }
  };
  
  // 停止摄像头
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
      setIsCameraActive(false);
    }
    clearCanvas();
  };
  
  // 从摄像头捕获帧
  const captureFrame = () => {
    if (!videoRef.current || !isCameraActive) {
      message.warning('请先启动摄像头');
      return;
    }
    
    try {
      // 创建临时Canvas捕获帧
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = videoRef.current.videoWidth;
      tempCanvas.height = videoRef.current.videoHeight;
      
      const ctx = tempCanvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      // 转换为DataURL
      const frameDataURL = tempCanvas.toDataURL('image/jpeg');
      
      // 设置为当前图像
      setCurrentImage(frameDataURL);
      
      // 创建File对象
      tempCanvas.toBlob((blob) => {
        if (blob) {
          const frameFile = new File([blob], 'camera-frame.jpg', { type: 'image/jpeg' });
          setUploadedFile(frameFile);
        }
      });
      
      clearCanvas();
      setDetectionResults([]);
      message.success('已捕获当前帧');
    } catch (error) {
      message.error('捕获帧失败：' + error.message);
      console.error('帧捕获错误:', error);
    }
  };

  return (
    <Layout style={{ background: 'transparent' }}>
      <Content className="main-wrap" style={{ padding: '20px' }}>
        {/* 项目标题与介绍 */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={1} style={{ color: 'var(--theme-primary)', marginBottom: '16px' }}>
            🐺 哈士奇跟踪系统
          </Title>
          <Paragraph style={{ maxWidth: '800px', margin: '0 auto', color: 'var(--theme-text)' }}>
            基于YOLO算法的实时哈士奇检测与跟踪系统，专注于宠物犬的智能识别
          </Paragraph>
        </div>

        {/* 项目特点 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
          <Col xs={24} sm={12} lg={8}>
            <Card 
              hoverable
              style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ fontSize: '24px', color: 'var(--theme-primary)' }}>⚡</div>
                <Title level={4} style={{ margin: '8px 0' , color: 'var(--theme-primary)'}}>实时检测</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px' , color: 'var(--theme-text)'}}>
                  基于YOLO算法实现毫秒级哈士奇检测，支持视频流实时处理
                </Paragraph>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card 
              hoverable
              style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ fontSize: '24px', color: 'var(--theme-primary)' }}>🔍</div>
                <Title level={4} style={{ margin: '8px 0' , color: 'var(--theme-primary)'}}>高精度识别</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px' , color: 'var(--theme-text)'}}>
                  专门针对哈士奇的特征优化，检测准确率达95%以上
                </Paragraph>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card 
              hoverable
              style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ fontSize: '24px', color: 'var(--theme-primary)' }}>🎯</div>
                <Title level={4} style={{ margin: '8px 0' , color: 'var(--theme-primary)'}}>目标跟踪</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px' , color: 'var(--theme-text)'}}>
                  支持多目标同时跟踪，实时标注哈士奇在视频中的位置
                </Paragraph>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* 核心功能演示 */}
        <Card 
          title="功能演示" 
          style={{ marginBottom: '32px', background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
          extra={<InfoCircleOutlined />}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={5}>上传图像</Title>
                <Dragger 
                  name="image" 
                  multiple={false}
                  beforeUpload={handleImageUpload}
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '2px dashed var(--theme-card-border)'
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
                    <UploadOutlined style={{ fontSize: '48px', color: 'var(--theme-primary)' }} />
                    <p>点击或拖拽图像文件到此处上传</p>
                    <Text type="secondary">支持 JPG、PNG、JPEG 格式</Text>
                  </Space>
                </Dragger>

                <Space wrap>
                  {/* 摄像头控制按钮 */}
                  {!isCameraActive ? (
                    <Button 
                      icon={<CameraOutlined />}
                      onClick={startCamera}
                      disabled={isProcessing}
                    >
                      打开摄像头
                    </Button>
                  ) : (
                    <>
                      <Button 
                        icon={<CameraOutlined />}
                        onClick={captureFrame}
                        disabled={isProcessing}
                      >
                        捕获帧
                      </Button>
                      <Button 
                        danger
                        icon={<CameraOutlined />}
                        onClick={stopCamera}
                        disabled={isProcessing}
                      >
                        关闭摄像头
                      </Button>
                    </>
                  )}
                  
                  {/* 检测按钮 */}
                  <Button 
                    type="primary" 
                    icon={<PlayCircleOutlined />} 
                    onClick={handleDetection}
                    loading={isProcessing}
                    disabled={!currentImage}
                  >
                    {isProcessing ? '检测中...' : '开始检测'}
                  </Button>
                  
                  {/* 下载和清空按钮 */}
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    loading={isProcessing}
                    disabled={!detectionResults.length}
                  >
                    下载结果
                  </Button>
                  <Button 
                    danger
                    onClick={clearCanvas}
                    disabled={!detectionResults.length}
                  >
                    清空
                  </Button>
                </Space>
              </Space>
            </Col>

            <Col xs={24} lg={12}>
              <Title level={5}>检测结果</Title>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: '300px',
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                padding: '16px'
              }}>
                {/* 显示区域：图像或摄像头 */}
                {isCameraActive ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {/* 摄像头视频 */}
                    <video 
                      ref={videoRef} 
                      style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '4px', display: 'block', transform: 'scaleX(-1)' }}
                      autoPlay 
                      playsInline 
                    />
                    
                    {/* 摄像头捕获帧的检测结果 */}
                    {currentImage && (
                      <>
                        <img 
                          ref={imgRef}
                          src={currentImage} 
                          alt="捕获的帧" 
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            maxWidth: '100%', 
                            maxHeight: '500px', 
                            borderRadius: '4px',
                            display: 'none' // 隐藏图像，只用于绘制检测框
                          }}
                        />
                        <canvas 
                          ref={canvasRef} 
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0,
                            pointerEvents: 'none',
                            width: '100%',
                            height: '100%'
                          }}
                        />
                      </>
                    )}
                  </div>
                ) : currentImage ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img 
                      ref={imgRef}
                      src={currentImage} 
                      alt="上传的图像" 
                      style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '4px', display: 'block' }}
                    />
                    <canvas 
                      ref={canvasRef} 
                      style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0,
                        pointerEvents: 'none',
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: 'var(--theme-text-secondary)' }}>
                    <CameraOutlined style={{ fontSize: '48px', marginBottom: '8px' }} />
                    <p>上传图像或打开摄像头开始检测</p>
                  </div>
                )}
              </div>

              {detectionResults.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Title level={5}>检测到的对象</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {detectionResults.map((result, index) => (
                      <div 
                        key={index || result.id}
                        style={{ 
                          padding: '8px 12px', 
                          background: 'rgba(255, 0, 0, 0.1)',
                          borderRadius: '4px',
                          border: '1px solid rgba(255, 0, 0, 0.3)'
                        }}
                      >
                        <Text strong>{result.className}</Text> - 置信度: {result.confidence.toFixed(2)}
                      </div>
                    ))}
                  </Space>
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* 技术架构 */}
        <Card 
          title="技术架构" 
          style={{ marginBottom: '32px', background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <div style={{ 
                padding: '16px', 
                background: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🧠</div>
                <Title level={5} style={{ color: 'var(--theme-primary)'}}>YOLO v8</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px' , color: 'var(--theme-text)'}}>
                  使用最新的YOLO v8算法进行目标检测，实现高精度、低延迟的识别效果
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <div style={{ 
                padding: '16px', 
                background: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚙️</div>
                <Title level={5} style={{ color: 'var(--theme-primary)'}}>OpenCV</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px' , color: 'var(--theme-text)'}}>
                  集成OpenCV库进行图像处理和视频分析，支持实时跟踪功能
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <div style={{ 
                padding: '16px', 
                background: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🚀</div>
                <Title level={5} style={{ color: 'var(--theme-primary)'}}>React</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px' , color: 'var(--theme-text)'}}>
                  基于React框架构建交互式用户界面，提供流畅的操作体验
                </Paragraph>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 项目链接 */}
        <div style={{ textAlign: 'center' }}>
          <Space wrap>
            <Button 
              type="primary" 
              icon={<GithubOutlined />}
              onClick={() => window.open('https://github.com', '_blank')}
            >
              GitHub仓库
            </Button>
            <Button 
              icon={<InfoCircleOutlined />}
              onClick={() => window.location.href = '/post/1770476327761'}
            >
              项目文档
            </Button>
            <Button 
              onClick={() => window.history.back()}
            >
              返回项目列表
            </Button>
          </Space>
        </div>
      </Content>
    </Layout>
  );
}